import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronDown,
  MapPin,
  Pencil,
  AlertTriangle,
} from "lucide-react";
import { resetCart } from "@/store/slices/cartSlice";
import { Link, useNavigate } from "react-router-dom";
import { formatPrice } from "@/utils/utils";
import useApi from "@/utils/useApi";
import apis from "@/utils/apis";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import CartCard from "./components/CartCard";
import AddressSelectorModal from "@/components/AddressSelectorModal";
import AddressModal from "@/components/AddressModal";
import { ERROR_CODES, getErrorMessage } from "@/utils/errorConstants";
import useAddress from "@/hooks/useAddress";
import useGpsLocation from "@/hooks/useGpsLocation";
import { setGpsLocation } from "@/store/slices/userSlice";

const FREE_DELIVERY_THRESHOLD = 500;

const CartPage = () => {
  const { t } = useTranslation();
  const paymentMethods = [
    { id: "cod", name: t("cart.paymentMethod.cashOnDelivery") },
  ];
  const dispatch = useDispatch();
  const cartProducts = useSelector((state) => state.cart.cartProducts);
  const user = useSelector((state) => state.user.userInfo);
  const martId = user?.defaultAddress?.nearby?.mart._id;
  const activeMembership = user?.activePremiumMembership;
  const [subtotal, setSubtotal] = useState(0);
  const [deliveryCharge, setDeliveryCharge] = useState(
    user?.defaultAddress?.nearby?.deliveryCharge
  );
  const [paymentMethod, setPaymentMethod] = useState(paymentMethods[0].id);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { callApi: callCreateOrder, isLoading: createOrderLoading } = useApi();
  const { callApi: callValidatePromotion, isLoading: isValidatingPromo } = useApi();
  const selectedPaymentMethod = paymentMethods.find(
    (m) => m.id === paymentMethod
  );
  const navigate = useNavigate();
  const gpsLocation = useSelector((state) => state?.user?.gpsLocation);
  const { getLocation } = useGpsLocation();

  const [isAddressListModalOpen, setIsAddressListModalOpen] = useState(false);
  const [isAddAddressModalOpen, setIsAddAddressModalOpen] = useState(false);
  const [pausedProducts, setPausedProducts] = useState([]);
  const [appliedMembership, setAppliedMembership] = useState("");
  const membershipAutoApplied = useRef(false);
  const [couponInput, setCouponInput] = useState("");
  const [voucherInput, setVoucherInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState("");
  const [promotionBreakdown, setPromotionBreakdown] = useState(null);

  const {
    userDefaultAddress,
    setSelectedAddress,
    handleAddressComplete,
    fetchAddresses,
  } = useAddress();

  const isFreeDelivery = subtotal >= FREE_DELIVERY_THRESHOLD;

  const buildPromotionProducts = () =>
    cartProducts.map((product) => ({
      stock: product?.stockId,
      quantity: product?.quantity,
    }));

  const fetchPromotionBreakdown = async (
    membershipCode,
    couponCode,
    voucherCode,
    delivery
  ) => {
    if (!martId || !buildPromotionProducts().length) return null;

    const finalDelivery =
      delivery ??
      (subtotal >= FREE_DELIVERY_THRESHOLD
        ? 0
        : user?.defaultAddress?.nearby?.deliveryCharge || 0);

    const response = await callValidatePromotion({
      api: apis.validatePromotion,
      body: {
        martId,
        products: buildPromotionProducts(),
        deliveryCharge: finalDelivery,
        membershipCode: membershipCode || undefined,
        couponCode: couponCode || undefined,
        voucherCode: voucherCode || undefined,
      },
    });
    return response?.breakdown ?? null;
  };

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    try {
      const breakdown = await fetchPromotionBreakdown(
        appliedMembership || undefined,
        code,
        appliedVoucher || undefined
      );
      setAppliedCoupon(code);
      setPromotionBreakdown(breakdown);
      toast.success(t("cart.orderSummary.couponDiscount"));
    } catch (err) {
      if (err?.code === "RATE_LIMIT_EXCEEDED") {
        toast.error(err?.message || "Too many attempts. Please wait.");
      } else {
        toast.error(err?.message || t("cart.orderSummary.invalidCoupon"));
      }
    }
  };

  const handleApplyVoucher = async () => {
    const code = voucherInput.trim().toUpperCase();
    if (!code) return;
    try {
      const breakdown = await fetchPromotionBreakdown(
        appliedMembership || undefined,
        appliedCoupon || undefined,
        code
      );
      setAppliedVoucher(code);
      setPromotionBreakdown(breakdown);
      toast.success(t("cart.orderSummary.voucherDiscount"));
    } catch (err) {
      if (err?.code === "RATE_LIMIT_EXCEEDED") {
        toast.error(err?.message || "Too many attempts. Please wait.");
      } else {
        toast.error(err?.message || t("cart.orderSummary.invalidVoucher"));
      }
    }
  };

  const handleRemoveCoupon = async () => {
    setAppliedCoupon("");
    setCouponInput("");
    if (appliedVoucher || appliedMembership) {
      try {
        const breakdown = await fetchPromotionBreakdown(
          appliedMembership || undefined,
          undefined,
          appliedVoucher || undefined
        );
        setPromotionBreakdown(breakdown);
      } catch {
        setAppliedVoucher("");
        setVoucherInput("");
        setPromotionBreakdown(null);
      }
    } else {
      setPromotionBreakdown(null);
    }
  };

  const handleRemoveVoucher = async () => {
    setAppliedVoucher("");
    setVoucherInput("");
    if (appliedCoupon || appliedMembership) {
      try {
        const breakdown = await fetchPromotionBreakdown(
          appliedMembership || undefined,
          appliedCoupon || undefined,
          undefined
        );
        setPromotionBreakdown(breakdown);
      } catch {
        setAppliedCoupon("");
        setCouponInput("");
        setPromotionBreakdown(null);
      }
    } else {
      setPromotionBreakdown(null);
    }
  };

  const basePayableTotal = subtotal + (deliveryCharge || 0);
  const payableTotal =
    promotionBreakdown?.totalCollectedAmount ??
    promotionBreakdown?.totalAmount ??
    basePayableTotal;

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const selectPaymentMethod = (methodId) => {
    setPaymentMethod(methodId);
    setIsDropdownOpen(false);
  };

  const createOrder = async () => {
    const {
      name,
      mobile,
      country,
      state,
      city,
      pin_code: pinCode,
      address,
      nearby,
    } = userDefaultAddress || {};

    if (!address) {
      setSelectedAddress(userDefaultAddress);
      setIsAddAddressModalOpen(true);
      return;
    }

    if (nearby?.isDeleted) {
      toast.error(t("cart.deliveryAddress.locationDeleted"), {
        duration: 5000,
      });
      setIsAddressListModalOpen(true);
      return;
    }

    const orderItems = cartProducts.map((product) => ({
      product: product?._id,
      variant: product?.variant?._id,
      stock: product?.stockId,
      quantity: product?.quantity,
      price: product?.sellingPrice,
      mrp: product?.price,
    }));

    try {
      await callCreateOrder({
        api: apis.createOrder,
        body: {
          martId,
          products: orderItems,
          deliveryCharge,
          paymentMethod,
          shippingAddress: {
            name,
            mobile,
            country,
            state,
            city,
            pinCode,
            address,
            nearbyLocation: nearby?.nearbyLocation,
          },
          ...(gpsLocation ? { gpsLocation } : {}),
          membershipCode: appliedMembership || undefined,
          couponCode: appliedCoupon || undefined,
          voucherCode: appliedVoucher || undefined,
        },
      });
      toast.success(t("cart.orderSuccess"));
      navigate("/order-success");
      dispatch(resetCart());
    } catch (err) {
      console.log(err);
      console.error(err);

      if (err?.code === ERROR_CODES.STOCK_PAUSED_WITH_PAUSE_AFTER.code) {
        const errors = Array.isArray(err.errors) ? err.errors : [err];
        const pausedStockIds = errors.map((error) => error.stockId);

        setPausedProducts((prev) => [...new Set([...prev, ...pausedStockIds])]);

        const errorMessage = getErrorMessage(
          ERROR_CODES.STOCK_PAUSED_WITH_PAUSE_AFTER.code,
          err,
          errors.length
        );

        toast.error(errorMessage, { duration: 6000 });
      } else if (err?.message) {
        toast.error(err.message);
      } else {
        toast.error(t("cart.orderFailure"));
      }
    }
  };

  useEffect(() => {
    const calculatedSubtotal = cartProducts.reduce((acc, item) => {
      return acc + item?.sellingPrice * item.quantity;
    }, 0);
    setSubtotal(calculatedSubtotal);

    const finalDeliveryCharge =
      calculatedSubtotal >= FREE_DELIVERY_THRESHOLD
        ? 0
        : user?.defaultAddress?.nearby?.deliveryCharge;
    setDeliveryCharge(finalDeliveryCharge || 0);

    const currentStockIds = cartProducts.map((product) => product.stockId);
    setPausedProducts((prev) =>
      prev.filter((stockId) => currentStockIds.includes(stockId))
    );
  }, [cartProducts, user?.defaultAddress?.nearby?.deliveryCharge]);

  useEffect(() => {
    if (!activeMembership?.code || membershipAutoApplied.current) return;
    if (!cartProducts.length || !martId) return;

    membershipAutoApplied.current = true;
    setAppliedMembership(activeMembership.code);

    void (async () => {
      try {
        const breakdown = await fetchPromotionBreakdown(activeMembership.code);
        if (breakdown) setPromotionBreakdown(breakdown);
      } catch {
        // Membership auto-apply is best-effort
      }
    })();
  }, [activeMembership?.code, cartProducts.length, martId]);

  useEffect(() => {
    if (!appliedMembership && !appliedCoupon && !appliedVoucher) return;
    if (!cartProducts.length || !martId) return;

    const timer = setTimeout(() => {
      void (async () => {
        try {
          const finalDelivery =
            subtotal >= FREE_DELIVERY_THRESHOLD
              ? 0
              : user?.defaultAddress?.nearby?.deliveryCharge || 0;
          const breakdown = await fetchPromotionBreakdown(
            appliedMembership || undefined,
            appliedCoupon || undefined,
            appliedVoucher || undefined,
            finalDelivery
          );
          setPromotionBreakdown(breakdown);
        } catch {
          setPromotionBreakdown(null);
        }
      })();
    }, 400);

    return () => clearTimeout(timer);
  }, [
    cartProducts,
    subtotal,
    appliedMembership,
    appliedCoupon,
    appliedVoucher,
    martId,
    user?.defaultAddress?.nearby?.deliveryCharge,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const isOrderEnabled =
      cartProducts?.length > 0 &&
      !user.isMartClose &&
      !userDefaultAddress?.nearby?.isDeleted;

    if (isOrderEnabled) {
      const refreshGps = async () => {
        try {
          const coords = await getLocation();
          if (coords) {
            dispatch(setGpsLocation(coords));
            console.log("[Cart] GPS refreshed:", coords);
          }
        } catch (err) {
          console.log("[Cart] GPS refresh failed, will use app-start GPS:", err);
        }
      };
      refreshGps();
    }
  }, [
    cartProducts?.length,
    user.isMartClose,
    userDefaultAddress?.nearby?.isDeleted,
    getLocation,
    dispatch,
  ]);

  const originalTotal = cartProducts.reduce(
    (acc, item) => acc + (item.price || item.sellingPrice) * item.quantity,
    0
  );
  const userSaved = originalTotal - subtotal;

  const renderCartItems = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white section rounded-lg shadow-md p-6 mb-6"
    >
      <h2 className="text-xl font-semibold mb-4">{t("cart.yourItems")}</h2>
      {cartProducts.map((product) => (
        <CartCard
          key={product?.stockId || product?._id}
          product={product}
          isPaused={pausedProducts.includes(product?.stockId)}
        />
      ))}
    </motion.div>
  );

  const renderDeliveryAddress = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="bg-white section rounded-lg shadow-md p-6 mb-6"
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">
          {t("cart.deliveryAddress.title")}
        </h2>
        <button
          onClick={() => setIsAddressListModalOpen(true)}
          className="text-primary-100 hover:text-primary-600 transition-colors flex items-center gap-1"
        >
          <Pencil className="w-4 h-4" />
          <span className="text-sm font-medium">
            {t("cart.deliveryAddress.change")}
          </span>
        </button>
      </div>

      {userDefaultAddress ? (
        <>
          {userDefaultAddress.nearby?.isDeleted && (
            <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-400 dark:border-orange-600 rounded-md">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                    {t("cart.deliveryAddress.locationUnavailable")}
                  </p>
                  <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                    {t("cart.deliveryAddress.pleaseUpdateAddress")}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-primary-100 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="font-semibold text-gray-900 mb-1">
                {userDefaultAddress.name}
              </p>
              <p className="text-sm text-gray-600 mb-1">
                {userDefaultAddress.address}, {userDefaultAddress.city},{" "}
                {userDefaultAddress.state} - {userDefaultAddress.pin_code}
              </p>
              {userDefaultAddress.landmark && (
                <p className="text-sm text-gray-500 mb-1">
                  Landmark: {userDefaultAddress.landmark}
                </p>
              )}
              <p className="text-sm text-gray-600">
                Phone: {userDefaultAddress.mobile}
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-4">
          <p className="text-gray-600 mb-3">
            {t("cart.deliveryAddress.noAddress")}
          </p>
          <button
            onClick={() => setIsAddressListModalOpen(true)}
            className="text-primary-100 hover:text-primary-600 transition-colors font-medium"
          >
            {t("cart.deliveryAddress.addAddress")}
          </button>
        </div>
      )}
    </motion.div>
  );

  const renderOrderSummary = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="bg-white section rounded-lg shadow-md p-6"
    >
      <h2 className="text-xl font-semibold mb-4">
        {t("cart.orderSummary.title")}
      </h2>
      {originalTotal > subtotal && (
        <div className="flex justify-between mb-2">
          <span className="text-gray-600">
            {t("cart.orderSummary.originalTotal")}
          </span>
          <span className="text-gray-500 line-through">
            ₹{formatPrice(originalTotal?.toFixed(2))}
          </span>
        </div>
      )}
      <div className="flex justify-between mb-2">
        <span className="text-gray-600">{t("cart.orderSummary.subtotal")}</span>
        <span className="font-semibold">
          ₹{formatPrice(subtotal?.toFixed(2))}
        </span>
      </div>
      <div className="flex justify-between mb-2">
        <span className="text-gray-600">
          {t("cart.orderSummary.deliveryCharge")}
        </span>
        <div className="flex flex-row-reverse items-center gap-2">
          {isFreeDelivery ? (
            <>
              <span className="font-semibold text-green-600">
                {t("cart.orderSummary.freeDelivery")}
              </span>
              {user?.defaultAddress?.nearby?.deliveryCharge > 0 && (
                <span className="text-xs text-gray-500 line-through">
                  ₹
                  {formatPrice(
                    user?.defaultAddress?.nearby?.deliveryCharge?.toFixed(2)
                  )}
                </span>
              )}
            </>
          ) : (
            <span className="font-semibold">
              ₹{formatPrice(deliveryCharge?.toFixed(2))}
            </span>
          )}
        </div>
      </div>
      {!isFreeDelivery && subtotal > 0 && (
        <div className="text-sm text-green-600 mb-2">
          {t("cart.orderSummary.addMoreForFreeDelivery", {
            amount: formatPrice(
              (FREE_DELIVERY_THRESHOLD - subtotal)?.toFixed(2)
            ),
          })}
        </div>
      )}

      {activeMembership?.code && (
        <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
            Premium membership
            {activeMembership.planName ? ` (${activeMembership.planName})` : ""}
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
            {appliedMembership
              ? `${activeMembership.discountPercent}% off applied automatically`
              : `${activeMembership.discountPercent}% member discount available`}
          </p>
        </div>
      )}

      {(promotionBreakdown?.membershipDiscountAmount > 0 ||
        promotionBreakdown?.couponDiscountAmount > 0 ||
        promotionBreakdown?.voucherDiscountAmount > 0) && (
        <div className="space-y-2 mb-3 border-t pt-3">
          {promotionBreakdown?.membershipDiscountAmount > 0 && (
            <div className="flex justify-between text-amber-700">
              <span>
                Premium discount ({promotionBreakdown.membershipCode})
              </span>
              <span>
                - ₹
                {formatPrice(
                  promotionBreakdown.membershipDiscountAmount?.toFixed(2)
                )}
              </span>
            </div>
          )}
          {promotionBreakdown?.couponDiscountAmount > 0 && (
            <div className="flex justify-between text-green-700">
              <span>Coupon ({promotionBreakdown.couponCode})</span>
              <span>
                - ₹
                {formatPrice(
                  promotionBreakdown.couponDiscountAmount?.toFixed(2)
                )}
              </span>
            </div>
          )}
          {promotionBreakdown?.voucherDiscountAmount > 0 && (
            <div className="flex justify-between text-green-700">
              <span>Voucher ({promotionBreakdown.voucherCode})</span>
              <span>
                - ₹
                {formatPrice(
                  promotionBreakdown.voucherDiscountAmount?.toFixed(2)
                )}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
        <p className="text-sm font-semibold text-gray-800">
          {t("cart.orderSummary.couponCode")} /{" "}
          {t("cart.orderSummary.voucherCode")}
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase"
            placeholder={t("cart.orderSummary.couponCode")}
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value)}
            disabled={Boolean(appliedCoupon) || isValidatingPromo}
          />
          {appliedCoupon ? (
            <button
              type="button"
              onClick={handleRemoveCoupon}
              className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg"
            >
              {t("cart.orderSummary.remove")}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleApplyCoupon}
              disabled={isValidatingPromo}
              className="px-3 py-2 text-sm bg-primary-100 text-white rounded-lg disabled:opacity-50"
            >
              {t("cart.orderSummary.apply")}
            </button>
          )}
        </div>
        {appliedCoupon && promotionBreakdown?.couponDiscountAmount > 0 && (
          <p className="text-xs text-green-600">
            {appliedCoupon}: -₹{promotionBreakdown.couponDiscountAmount}
          </p>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase"
            placeholder={t("cart.orderSummary.voucherCode")}
            value={voucherInput}
            onChange={(e) => setVoucherInput(e.target.value)}
            disabled={Boolean(appliedVoucher) || isValidatingPromo}
          />
          {appliedVoucher ? (
            <button
              type="button"
              onClick={handleRemoveVoucher}
              className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg"
            >
              {t("cart.orderSummary.remove")}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleApplyVoucher}
              disabled={isValidatingPromo}
              className="px-3 py-2 text-sm bg-primary-100 text-white rounded-lg disabled:opacity-50"
            >
              {t("cart.orderSummary.apply")}
            </button>
          )}
        </div>
        {appliedVoucher && promotionBreakdown?.voucherDiscountAmount > 0 && (
          <p className="text-xs text-green-600">
            {appliedVoucher}: -₹{promotionBreakdown.voucherDiscountAmount}
          </p>
        )}
      </div>

      <div className="flex justify-between font-bold text-lg mt-4">
        <span>{t("cart.orderSummary.total")}</span>
        <span>₹{formatPrice(payableTotal?.toFixed(2))}</span>
      </div>
      {userSaved > 0 && (
        <div className="flex justify-between mt-2">
          <span className="text-green-700 font-medium">
            {t("cart.orderSummary.youSaved")}
          </span>
          <span className="text-green-700 font-semibold">
            ₹{formatPrice(userSaved?.toFixed(2))}
          </span>
        </div>
      )}
    </motion.div>
  );

  const renderPaymentMethodDropdown = () => (
    <div className="relative max-w-64 w-full" ref={dropdownRef}>
      {isDropdownOpen && (
        <div className="absolute z-10 bottom-full mb-1 w-full bg-white shadow-lg rounded-md py-1 ring-1 ring-black ring-opacity-5 section">
          {paymentMethods.map((method) => (
            <button
              key={method.id}
              onClick={() => selectPaymentMethod(method.id)}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              {method.name}
            </button>
          ))}
        </div>
      )}
      <button
        onClick={toggleDropdown}
        className="flex items-center justify-between w-full max-w-64 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-100 section"
      >
        {selectedPaymentMethod?.name}
        <ChevronDown className="w-5 h-5 ml-2" />
      </button>
    </div>
  );

  return (
    <div className="flex-grow flex flex-col font-sans">
      <header className="bg-white shadow-sm sticky top-0 z-50 section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-primary-100 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold ml-4">{t("cart.header")}</h1>
        </div>
      </header>

      <main className="grow p-3">
        {cartProducts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-md p-6 mb-6 text-center section"
          >
            <h2 className="text-2xl font-semibold mb-4">
              {t("cart.emptyCartMessage")}
            </h2>
            <p className="text-gray-600 mb-4">
              {t("cart.emptyCartDescription")}
            </p>
            <Link
              to="/"
              className="bg-primary-100 text-white px-6 py-2 rounded-full font-semibold hover:bg-primary-100 transition-colors"
            >
              {t("cart.startShopping")}
            </Link>
          </motion.div>
        ) : (
          <>
            {renderCartItems()}
            {renderDeliveryAddress()}
            {renderOrderSummary()}
          </>
        )}
      </main>

      <footer className="bg-white shadow-md bottom-0 left-0 right-0 section">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col gap-3">
          {user.isMartClose && cartProducts?.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0" />
              <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                {t("cart.martClosed")}
              </p>
            </div>
          )}
          <div className="flex justify-between items-center gap-6">
            {renderPaymentMethodDropdown()}
            <button
              className="bg-primary-100 text-white px-6 py-2 rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap h-10 min-w-[140px] flex justify-center items-center"
              disabled={
                !cartProducts?.length ||
                user.isMartClose ||
                userDefaultAddress?.nearby?.isDeleted
              }
              onClick={createOrder}
            >
              {createOrderLoading ? (
                <div className="loader !text-white" />
              ) : (
                t("cart.placeOrder")
              )}
            </button>
          </div>
        </div>
      </footer>

      <AddressSelectorModal
        isOpen={isAddressListModalOpen}
        onClose={() => setIsAddressListModalOpen(false)}
        title={t("cart.deliveryAddress.selectAddress")}
        setAsDefault={true}
        onAddressSelected={() => {}}
      />

      <AddressModal
        isOpen={isAddAddressModalOpen}
        onClose={() => setIsAddAddressModalOpen(false)}
        initialValues={userDefaultAddress || {}}
        isEdit={!!(userDefaultAddress?.pin_code && userDefaultAddress?.nearby)}
        onComplete={async (addressData) => {
          await handleAddressComplete(addressData);
          await fetchAddresses();
          setIsAddAddressModalOpen(false);
        }}
      />
    </div>
  );
};

export default CartPage;
